import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/95 backdrop-blur-md" : "bg-foreground-950/60 backdrop-blur-sm"
      }`}
    >
      <div className="flex items-center justify-between px-6 md:px-10 py-3.5">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-background-50">
              Mulebuy<span className="text-accent-400">Spreadsheets</span>
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-background-50/90 hover:text-background-50 hover:bg-background-50/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Home
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-background-50/90 hover:text-background-50 hover:bg-background-50/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Shop Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 rounded-lg overflow-hidden z-50 max-h-[480px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 border-b border-background-200/50 last:border-b-0"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="font-medium">{cat.name}</span>
                      <span className="block text-xs text-foreground-500 mt-0.5">{cat.description}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/mulebuy-spreadsheet-guide-2026"
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-background-50/90 hover:text-background-50 hover:bg-background-50/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line"></i>
              Shopping Guide
            </Link>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-background-50 text-foreground-950 hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            mulebuy spreadsheet
            <i className="ri-arrow-right-up-line"></i>
          </a>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line"></i>
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-background-50 cursor-pointer"
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile slide-in menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-[60px] z-40" ref={mobileMenuRef}>
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="relative bg-background-50 w-full max-h-[calc(100vh-60px)] overflow-y-auto">
            <div className="px-6 py-6 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-950 font-medium hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-home-4-line text-lg"></i>
                Home
              </Link>

              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Categories</p>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="font-medium">{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              <Link
                to="/mulebuy-spreadsheet-guide-2026"
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-foreground-950 font-medium hover:bg-background-100 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-book-open-line text-lg"></i>
                Shopping Guide
              </Link>

              <div className="pt-4 px-4 space-y-3">
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-semibold bg-foreground-950 text-background-50 cursor-pointer"
                >
                  mulebuy spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com/"
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-full text-sm font-semibold bg-primary-500 text-background-50 cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}